import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import { useToast } from "../components/toast/ToastProvider";
import { StoreState } from "../redux/reducers";
import { EmpReport } from "../types/EmpReport";
import {
  addEmpReportAction,
  deleteEmpReportAction,
  editEmpReportAction,
  fetchEmpReportsAction,
} from "../redux/actions/empReportAction";
import EmployeeReportForm from "../components/EmployeeReportForm";
import EmployeeReportList from "../components/EmployeeReportList";
import { fetchTasks } from "../redux/actions/taskAction";
import { fetchProjects } from "../redux/actions/projectAction";
import { fetchEmployeesAction } from "../redux/actions/employeeActions";
import EmplyeeReportFilterAdmin from "../components/EmplyeeReprtFilterAdmin";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "react-feather";
import { LocalFormatDate } from "../config/functions";

const EmployeeReportPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const empReportList = useSelector(
    (state: StoreState) => state.employeeReport,
  );
  const [selectedEmpReport, setSelectedEmpReport] = useState<EmpReport | null>(
    null,
  );
  const user = useSelector((state: StoreState) => state.authUser);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const tasks = useSelector((state: StoreState) => state.task);
  const projects = useSelector((state: StoreState) => state.project);
  useEffect(() => {
    setLoading(true);
    dispatch(fetchEmpReportsAction()).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchTasks());
    dispatch(fetchEmployeesAction());
  }, [dispatch]);

  const handleEdit = (report: EmpReport) => {
    setSelectedEmpReport(report);
    setShowModal(true);
  };

  const addOrUpdateHandler = async (data: EmpReport) => {
    setLoading(true);
    try {
      let message;

      if (selectedEmpReport) {
        message = await dispatch(editEmpReportAction(data.id, data));
        toast("Employee Report updated successfully");
      } else {
        message = await dispatch(addEmpReportAction(data));
      }

      toast(message);
      dispatch(fetchEmpReportsAction());
      setSelectedEmpReport(null);
      setShowModal(false);
    } catch (error: any) {
      console.error("EmpReport Save Error:", error);
      toast(error?.toString() || "Error in Save EmpReport");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure?")) {
      setLoading(true);
      await dispatch(deleteEmpReportAction(id));
      toast("EmpReport deleted successfully");
      await dispatch(fetchEmpReportsAction());
      setLoading(false);
    }
  };

  const handleView = async (emp: EmpReport) => {
    setSelectedEmpReport(emp);
    setViewMode(true);
    setShowModal(true);
  };

  const getProjectNameById = (projectId: Number): string => {
    const project = projects.find((p) => p.id === projectId);
    return project ? project.name : "unknown Projects";
  };

  const getTaskById = (taskId: Number): string => {
    const task = tasks.find((t) => t.id === taskId);
    return task ? task.title : "unknown Tasks";
  };

  const downloadDailyReportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const title = "Daily Report Details";
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleWidth = doc.getTextWidth(title);

    doc.setFontSize(14);
    doc.text(title, (pageWidth - titleWidth) / 2, 10);

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");

    const filteredEmpReport = applyFilters.filter(
      (emp) => emp.employeeId !== null,
    );

    const headers = [
      "S.No",
      "Employee ID",
      "Date",
      "Project",
      "Task",
      "Activity",
      "Hours Spent",
    ];

    const rows = filteredEmpReport.map((emp, i) => [
      i + 1,
      emp.employeeId || "-",
      LocalFormatDate(emp.date || "-"),
      getProjectNameById(emp.projectId),
      getTaskById(emp.taskId),
      emp.activity || "-",
      emp.hoursSpent || "-",
    ]);

    // Calculate total hours spent
    const totalHoursSpent = filteredEmpReport.reduce((sum, emp) => {
      const hours =
        typeof emp.hoursSpent === "number"
          ? emp.hoursSpent
          : parseFloat(emp.hoursSpent || "0");
      return sum + (isNaN(hours) ? 0 : hours);
    }, 0);

    // Add total row inside the table
    rows.push(["Total Hours Spent", "", "", "", "", "", totalHoursSpent]);

    autoTable(doc, {
      head: [headers],
      body: rows,
      theme: "grid",
      startY: 25,
      tableWidth: "auto",
      styles: {
        fontSize: 10,
        cellPadding: 2,
        overflow: "linebreak",
        textColor: [0, 0, 0],
        lineColor: 0,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 12,
        lineColor: 0,
        lineWidth: 0.1,
      },
      didDrawPage: () => {
        const pageNumber = doc.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.setFontSize(8);
        doc.text(`Page ${pageNumber}`, pageWidth - 40, pageHeight - 10);
      },
    });
    if (doc.getNumberOfPages() > 1) {
      doc.setPage(doc.getNumberOfPages());
    }
    const pageHeight = doc.internal.pageSize.getHeight();

    if ((doc as any).lastAutoTable.finalY + 20 > pageHeight - 20) {
      doc.addPage();
    }

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");

    doc.save("daily_report.pdf");
  };

  const [filtersEmp, setFiltersEmp] = useState({
    fromDate: "",
    toDate: "",
    project: "",
    task: "",
    employeeId: "",
  });

  const [filteredEmpRep, setFilteredEmpRep] = useState(filtersEmp);
  const [showResults, setShowResults] = useState(false);

  // Load from localStorage on first load
  useEffect(() => {
    const storedFilters = localStorage.getItem("EmplyeeReportFilterAdmin");
    if (storedFilters) {
      const parsedFilters = JSON.parse(storedFilters);
      setFiltersEmp(parsedFilters);
      setFilteredEmpRep(parsedFilters);
      setShowResults(true);
    }
  }, []);

  // Update filters when a field is changed
  const handleFilterChange = (field: string, value: string) => {
    setFiltersEmp((prev) => ({ ...prev, [field]: value }));
  };

  // On Find button click
  const handleFindFilters = () => {
    const { fromDate, toDate, project, task, employeeId } = filtersEmp;

    if (!project && (task || employeeId || fromDate || toDate)) {
      toast("Please select a Project first before using other filters.");
      return;
    }

    const noFiltersApplied =
      !fromDate && !toDate && !project && !task && !employeeId;

    if (noFiltersApplied) {
      toast("Please select at least one project before searching.");
      return;
    }

    localStorage.setItem(
      "EmplyeeReportFilterAdmin",
      JSON.stringify(filtersEmp),
    );
    setFilteredEmpRep(filtersEmp);
    setShowResults(true);
  };

  // Reset to default
  const handleResetFilters = () => {
    localStorage.removeItem("EmplyeeReportFilterAdmin");

    const resetState = {
      fromDate: "",
      toDate: "",
      project: "",
      task: "",
      employeeId: "",
    };

    setFiltersEmp(resetState);
    setFilteredEmpRep(resetState);
    setShowResults(false);
  };

  // Filter data
  const applyFilters = useMemo(() => {
    if (!showResults) return [];

    const { fromDate, toDate, project, task, employeeId } = filteredEmpRep;

    return empReportList.filter((empReport) => {
      const matchesFromDate =
        !fromDate || new Date(empReport.date) >= new Date(fromDate);
      const matchesToDate =
        !toDate || new Date(empReport.date) <= new Date(toDate);
      const matchesProject =
        !project || empReport.projectId?.toString() === project;
      const matchesEmployee =
        !employeeId || empReport.employeeId === employeeId;
      const matchesTask = !task || empReport.taskId?.toString() === task;

      return (
        matchesFromDate &&
        matchesToDate &&
        matchesEmployee &&
        matchesProject &&
        matchesTask
      );
    });
  }, [filteredEmpRep, empReportList, showResults]);

  const handleStatusToggle = async (report: EmpReport) => {
    const updatedStatus = report.status === "Approved" ? "Pending" : "Approved";
    const updatedReport = { ...report, status: updatedStatus };
    setLoading(true);
    try {
      await dispatch(editEmpReportAction(report.id, updatedReport));
      toast(`Status updated to ${updatedStatus}`);
      dispatch(fetchEmpReportsAction());

      // const updatedList = applyFilters(filtersEmp, empReportList);
      // setFilteredEmpReports(updatedList);
    } catch (error) {
      toast("Error updating status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Main>
      <PageContainer title="Daily  Report">
        <>
          {user.role !== "Admin" && (
            <button
              className="btn btn-primary mb-3"
              onClick={() => setShowModal(true)}
              disabled={loading}
            >
              {loading ? "Loading..." : "ADD"}
            </button>
          )}
        </>
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelectedEmpReport(null);
            setViewMode(false);
          }}
          headerText={selectedEmpReport ? "Update EmpReport" : "Add EmpReport"}
        >
          <>
            <EmployeeReportForm
              reportIdToEdit={selectedEmpReport?.id}
              onSave={addOrUpdateHandler}
              initialData={selectedEmpReport}
            />

            {viewMode && user.role === "User" && (
              <div className="d-flex justify-content-end mt-3">
                <button
                  className="btn btn-success"
                  onClick={() => setViewMode(false)}
                >
                  edit
                </button>
              </div>
            )}
          </>
        </Modal>

        <CollapseCard title="Daily Report Filter">
          <>
            <EmplyeeReportFilterAdmin
              filtersEmp={filtersEmp}
              onFind={handleFindFilters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
            />
          </>
        </CollapseCard>

        <CollapseCard title="Daily Report List">
          <EmployeeReportList
            empReportList={applyFilters}
            onEdit={user.role === "Admin" ? undefined : handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            onStatusToggle={handleStatusToggle}
          />
        </CollapseCard>
        <>
          {empReportList.length > 0 && (
            <div className="col-12 m-3 d-flex justify-content-end">
              <button
                className="btn btn-success mx-2"
                onClick={downloadDailyReportPDF}
                style={{ fontSize: "14px" }}
              >
                <Download style={{ width: "20px" }} /> PDF
              </button>
            </div>
          )}
        </>
      </PageContainer>
    </Main>
  );
};

export default EmployeeReportPage;
